import Delete from '@mui/icons-material/DeleteOutlineOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import UploadCsvIcon from '@mui/icons-material/FileUploadOutlined';
import InfoIcon from '@mui/icons-material/Info';
import SaveCsvFrom from '@mui/icons-material/SaveOutlined';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import Papa, { ParseResult } from 'papaparse';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Spreadsheet, { Point } from 'react-spreadsheet';
import Backend, { LookupFile } from '../backend/Backend';
import AquantCancelIcon from '../resources/images/aq-cancel.svg';
import { ReactComponent as CheckboxIcon } from '../resources/images/checkbox.svg';
import { ReactComponent as CheckedCheckboxIcon } from '../resources/images/checked-checkbox.svg';
import './LookupFileManager.scss';
import SITooltip from './SITooltip';
import SiDialog from './SiDialog';

interface LookupFileManagerProps {
    progress: Function;
    hidden: boolean;
}

interface FilteredFiles extends LookupFile {
    isChecked: boolean;
}

const LookupFileManager = (props: LookupFileManagerProps) => {
    const { t } = useTranslation();
    const { authState, oktaAuth } = useOktaAuth();
    const [filesList, setFilesList] = useState<LookupFile[] | null>(null);
    const [filteredFilesList, setFilteredFilesList] = useState<FilteredFiles[] | null>(null);
    const [fileFilterText, setFileFilterText] = useState<string | null>(null);
    const [selectedCsvFile, setSelectedCsvFile] = useState<LookupFile | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
    const [csv, setCsv] = useState([]);

    const [selectedRow, setSelectedRow] = useState<number>(-1);
    const [selectedColumn, setSelectedColumn] = useState<number>(-1);
    const [csvModified, setCsvModified] = useState<boolean>(false);

    useEffect(() => {
        if (props.hidden) {
            return;
        }

        if (!filesList) {
            fetchFiles();
        }
    });

    const fetchFiles = async () => {
        props.progress(true);
        try {
            const lookupResponse = await Backend.listLookupFiles(oktaAuth);
            const { files } = lookupResponse.data;

            setFilesList(files);
            setFilteredFilesList(files);
        } catch (err) {
            console.log(err);
        } finally {
            props.progress(false);
        }
    };

    const handleFileFilterChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
        let newFilteredFileList = [];
        const ft = e.target.value;

        if (ft && ft.trim().length > 0) {
            newFilteredFileList = filesList.filter(
                (item) =>
                    item.file_name.toLowerCase().search(ft.toLowerCase()) !== -1 ||
                    item.file_id.toLowerCase().search(ft.toLowerCase()) !== -1,
            );
        } else {
            newFilteredFileList = filesList;
        }

        setFilteredFilesList(newFilteredFileList);
        setFileFilterText(ft);
    };

    const handleSave = async () => {
        if (!csv || csv.length < 2) {
            return;
        }

        const csv_lines = [];
        let csv_line = [];

        // header row
        const hrd = csv[0];

        for (const hi in hrd) {
            if (hrd[hi]) {
                csv_line.push(hrd[hi].value);
            }
        }

        csv_lines.push(csv_line.join(','));

        // lines
        for (let li = 1; li < csv.length; li++) {
            csv_line = [];
            for (const ci in csv[li]) {
                if (csv[li][ci]) {
                    csv_line.push(csv[li][ci].value);
                }
            }

            csv_lines.push(csv_line.join(','));
        }

        const csv_str = csv_lines.join('\n');

        if (selectedCsvFile.file_rowid) {
            props.progress(true);
            await Backend.createLookupFile(selectedCsvFile.file_id, selectedCsvFile.file_name, csv_str, oktaAuth);
            props.progress(false);
        } else {
            alert('error!'); // should never happen
        }
    };

    const updateCsv = async (data) => {
        setCsv(data);
        setCsvModified(true);
    };

    const handleDownloadFileAction = async () => {
        props.progress(true);
        await handleDownloadFiles();
        props.progress(false);
    };

    const handleSpreadsheetSelect = async (selected: Point[]) => {
        /*if(selected.length === 0) {
            setSelectedRow(-1);
            setSelectedColumn(-1);
        }*/
    };

    const handleSpreadsheetCellActivated = async (selected: Point) => {
        if (selected.column === 0) {
            setSelectedRow(selected.row);
        }

        if (selected.row === 0) {
            setSelectedColumn(selected.column);
        }
    };

    const handleSelectedFileChanged = async (currFile: LookupFile) => {
        setSelectedCsvFile(currFile);
        setCsvModified(false);
        props.progress(true);
        let fileContent = currFile.file_content;

        if (!fileContent) {
            const response = await Backend.getLookupFileContent(currFile.file_name, oktaAuth);
            const existingFileIndex = filesList.findIndex((file) => file.file_name === currFile.file_name);
            const existingFilterFileIndex = filteredFilesList.findIndex(
                (file) => file.file_name === currFile.file_name,
            );
            fileContent = response.data.file_content;

            if (existingFileIndex !== -1) {
                filesList[existingFileIndex].file_content = fileContent;
                filteredFilesList[existingFilterFileIndex].file_content = fileContent;
            }

            setFilesList(filesList);
            setFilteredFilesList(filteredFilesList);
        }

        const results = Papa.parse(fileContent, { header: true });
        updateFromPapaResults(results);
        props.progress(false);
    };

    const updateFromPapaResults = (results: ParseResult<any>) => {
        const { meta } = results,
            { fields } = meta,
            { data } = results;
        const newCsv = [[]];

        // push the header row
        for (const f in fields) {
            if (fields[f]) {
                newCsv[0].push({ value: fields[f] });
            }
        }

        // data rows
        for (const i in data) {
            if (data[i]) {
                const d = data[i];
                const newRow = [];
                for (const j in fields) {
                    if (fields[j]) {
                        newRow.push({ value: d[fields[j]] });
                    }
                }

                newCsv.push(newRow);
            }
        }

        setCsv(newCsv);
    };

    const [openImportDialog, setOpenImportDialog] = useState<boolean>(false);
    const [importFile, setImportFile] = useState<any | null>(null);
    const [importFilename, setImportFilename] = useState<string | null>(null);
    const [importFileId, setImportFileId] = useState<string | null>(null);

    const handleDismissImportDialog = () => {
        setOpenImportDialog(false);
        setImportFile(null);
        setImportFilename(null);
        setImportFileId(null);
    };

    const handleInitiateImportFile = () => {
        setOpenImportDialog(true);
    };

    const handleFileChecked = (event: React.ChangeEvent) => {
        filteredFilesList.forEach((file) => {
            if (file.file_name === event.target.id) {
                file.isChecked = !file.isChecked;
            }
        });

        const newFilteredFileList = [...filteredFilesList];
        setFilteredFilesList(newFilteredFileList);
    };

    const handleSelectAllFiles = (event) => {
        const isCheckedValue = event.target.checked;
        filteredFilesList.forEach((file) => {
            file.isChecked = isCheckedValue;
        });

        const newFilteredFileList = [...filteredFilesList];
        setFilteredFilesList(newFilteredFileList);
    };

    const handleDownloadFiles = async () => {
        const filesToDownload = filteredFilesList.filter((file) => file.isChecked).map((file) => file.file_name);
        const res = await Backend.downloadLookupfiles(filesToDownload, oktaAuth);
    };

    const handleDeleteFile = async () => {
        const filesToDelete = filteredFilesList.filter((file) => file.isChecked);
        const res = await Backend.deleteLookupFile(filesToDelete, oktaAuth);
        if (res.data) {
            const filesNotDeleted = filteredFilesList.filter((file) => !file.isChecked);
            const newFilteredFileList = [...filesNotDeleted];
            const newFilesFileList = [
                ...filesNotDeleted.map((file) => {
                    const newFile: LookupFile = {
                        file_content: file.file_content,
                        file_id: file.file_id,
                        file_name: file.file_name,
                        file_rowid: file.file_rowid,
                    };

                    return newFile;
                }),
            ];

            setFilteredFilesList(newFilteredFileList);
            setFilesList(newFilesFileList);
        }
    };

    const handleDeleteAction = async () => {
        props.progress(true);
        await handleDeleteFile();
        props.progress(false);
        setIsDeleteDialogOpen(false);
    };

    const handleConfirmImportFile = async () => {
        const reader = new FileReader();
        let newFilesList = [];
        let newFilteredFilesList = [];
        reader.readAsText(importFile);
        reader.onload = () => {
            props.progress(true);
            Backend.createLookupFile(importFileId, importFilename, reader.result.toString(), oktaAuth)
                .then(async (response) => {
                    const file_data = response.data;
                    const existingFileIndex = filesList.findIndex((file) => file.file_name === file_data.file_name);
                    const existingFilterFileIndex = filteredFilesList.findIndex(
                        (file) => file.file_name === file_data.file_name,
                    );

                    if (existingFileIndex !== -1) {
                        filesList[existingFileIndex].file_content = file_data.file_content;
                        filteredFilesList[existingFilterFileIndex].file_content = file_data.file_content;
                    } else {
                        filteredFilesList.unshift({
                            file_content: file_data.file_content,
                            file_id: file_data.file_id,
                            file_name: file_data.file_name,
                            file_rowid: file_data.file_rowid,
                            isChecked: false,
                        });
                    }

                    newFilesList = [...filesList];
                    setFilesList(newFilesList);
                    newFilteredFilesList = [...filteredFilesList];
                    setFilteredFilesList(newFilteredFilesList);
                    handleSelectedFileChanged(file_data);
                    props.progress(false);
                    handleDismissImportDialog();
                })
                .catch(() => {
                    props.progress(false);
                    alert(t('lookup-manager.create-error'));
                });
        };
    };

    const handleImportFileSelectionChanged = (e: any) => {
        const f = e.target.files[0];
        setImportFile(f);
        setImportFilename(f.name);
        setImportFileId(
            f.name
                .toLowerCase()
                .substring(0, f.name.length - 4)
                .replace(/[^A-Z0-9]/gi, '_'),
        );
    };

    return (
        <div>
            <Grid container className="LookupFileManager" spacing={2}>
                <Grid item xs={2} className="LeftPanel">
                    <Stack direction="column" spacing={1} height="100%" overflow="scroll">
                        <TextField
                            className="FileSearchInput"
                            placeholder={t('lookup-manager.search-files')}
                            value={fileFilterText}
                            size="small"
                            onChange={handleFileFilterChanged}
                        />
                        {filteredFilesList && filteredFilesList.length > 0 ? (
                            <List style={{ height: '100%', overflow: 'auto' }}>
                                <Stack alignItems="center" direction="row">
                                    <Checkbox
                                        disableRipple
                                        checkedIcon={<CheckedCheckboxIcon />}
                                        icon={<CheckboxIcon />}
                                        color="primary"
                                        onChange={handleSelectAllFiles}
                                        checked={
                                            filteredFilesList?.filter((file) => file.isChecked).length ===
                                            filteredFilesList.length
                                        }
                                        sx={{ padding: 0, marginRight: '12px' }}
                                    />
                                    <Typography sx={{ fontWeight: 400 }}>Select all files</Typography>
                                </Stack>
                                {filteredFilesList.map((item, index) => (
                                    <ListItemButton
                                        key={index}
                                        sx={{ paddingLeft: 0, alignItems: 'start' }}
                                        divider
                                        onClick={() => {
                                            handleSelectedFileChanged(item);
                                        }}
                                    >
                                        <Checkbox
                                            id={item.file_name}
                                            disableRipple
                                            checkedIcon={<CheckedCheckboxIcon />}
                                            icon={<CheckboxIcon />}
                                            color="primary"
                                            checked={!!item.isChecked}
                                            onChange={handleFileChecked}
                                            sx={{ padding: 0, marginRight: '12px', marginTop: '8px' }}
                                        />
                                        <Stack direction="column">
                                            <ListItemText primary={item.file_name} />
                                            <Typography className="FileId" variant="h6">
                                                {item.file_id}
                                            </Typography>
                                        </Stack>
                                    </ListItemButton>
                                ))}
                            </List>
                        ) : null}
                    </Stack>
                </Grid>
                <Grid item xs={10} className="RightPanel">
                    <Stack direction="column" rowGap={2} style={{ height: '100%' }}>
                        <Paper style={{ padding: '10px' }}>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<UploadCsvIcon />}
                                    onClick={handleInitiateImportFile}
                                >
                                    {t('lookup-manager.import')}
                                </Button>
                                <Divider orientation="vertical" flexItem />
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    disabled={
                                        !(
                                            filteredFilesList &&
                                            filteredFilesList.filter((file) => file.isChecked).length > 0
                                        )
                                    }
                                    onClick={handleDownloadFileAction}
                                >
                                    Download file
                                </Button>
                                <Divider orientation="vertical" flexItem />
                                <SITooltip title="Please select the file(s) you would like to delete from the Lookup Manager. The action is irreversible.">
                                    <Button
                                        size="small"
                                        disabled={
                                            !(
                                                filteredFilesList &&
                                                filteredFilesList.filter((file) => file.isChecked).length > 0
                                            )
                                        }
                                        variant="contained"
                                        color="error"
                                        startIcon={<Delete />}
                                        onClick={() => setIsDeleteDialogOpen(true)}
                                    >
                                        {t('lookup-manager.delete-file')}
                                    </Button>
                                </SITooltip>
                                <Divider orientation="vertical" flexItem />
                                <Button
                                    disabled={!csvModified}
                                    size="small"
                                    variant="contained"
                                    startIcon={<SaveCsvFrom />}
                                    onClick={handleSave}
                                >
                                    {t('lookup-manager.save')}
                                </Button>
                            </Stack>
                        </Paper>
                        <Spreadsheet
                            className="CsvFile"
                            data={csv}
                            onChange={updateCsv}
                            onSelect={handleSpreadsheetSelect}
                            onActivate={handleSpreadsheetCellActivated}
                        />
                    </Stack>
                </Grid>
            </Grid>

            <Dialog open={openImportDialog} onClose={handleDismissImportDialog}>
                <DialogTitle>{t('lookup-manager.import-title')}</DialogTitle>
                <DialogContent>
                    <Stack direction="row">
                        <Button size="small" variant="contained" startIcon={<UploadCsvIcon />} component="label">
                            {t('lookup-manager.select-file')}
                            <input hidden accept="text/csv" type="file" onChange={handleImportFileSelectionChanged} />
                        </Button>
                        {importFile ? <div className="ImportFileDetails">{importFile.name}</div> : null}
                    </Stack>
                    <TextField
                        margin="dense"
                        fullWidth
                        variant="standard"
                        placeholder={t('lookup-manager.file-name')}
                        value={importFilename}
                        onChange={(e) => setImportFilename(e.target.value)}
                        required
                    />
                    <TextField
                        margin="dense"
                        fullWidth
                        variant="standard"
                        placeholder={t('lookup-manager.file-id')}
                        value={importFileId}
                        onChange={(e) => setImportFileId(e.target.value)}
                        required
                    />
                    <Stack alignItems="center" marginTop="15px" direction="row">
                        <InfoIcon />
                        <Typography sx={{ marginLeft: 0.5 }}>
                            The existing file will be replaced if it exists in the same name
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDismissImportDialog}>{t('common.cancel')}</Button>
                    <Button
                        disabled={!importFile || !importFilename || !importFileId}
                        onClick={handleConfirmImportFile}
                    >
                        {t('lookup-manager.import')}
                    </Button>
                </DialogActions>
            </Dialog>
            <SiDialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                <DialogTitle>
                    <Stack className="DialogTitle">
                        <Typography className="DialogTitleText">Confirm Deletion</Typography>
                        <img
                            src={AquantCancelIcon}
                            onClick={() => {
                                setIsDeleteDialogOpen(false);
                            }}
                            alt="cancel icon"
                            className="CancelIcon"
                        />
                    </Stack>
                </DialogTitle>
                <Divider />
                <Paper className="DialogContentPaper">
                    <DialogContent className="ProcessDialogContent">
                        <DialogContentText
                            style={{ fontWeight: 500, marginBottom: '15px' }}
                            className="DialogContentTextNormal"
                        >
                            Are you sure you want to delete the selected{' '}
                            {filteredFilesList?.filter((file) => file.isChecked).length} files?
                        </DialogContentText>
                        <DialogContentText className="DialogContentTextNormal">
                            This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsDeleteDialogOpen(false)} disableRipple className="DialogActionBtn">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteAction}
                            variant="contained"
                            disableRipple
                            className="DialogActionBtnContained"
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Paper>
            </SiDialog>
        </div>
    );
};

export default LookupFileManager;
